import { FileButton, List, Loader, Space, Text } from '@mantine/core';
import { SupplierRequest, SupplierRequestInput } from '@scaleup/models';
import { useState } from 'react';
import {
  useDeleteSupplierRequestDocMutation,
  useGetSupplierRequestDocMutation,
  useUploadSupplierRequestDocMutation,
} from '../../../api';
import { Button } from '../../../ui';
import { SupplierRequestStep } from './types';

interface DocumentUploadProps {
  setCurrentStep: (step: SupplierRequestStep) => void;
  handleChangeValue: (
    key: keyof SupplierRequestInput,
    value: SupplierRequestInput[keyof SupplierRequestInput]
  ) => void;
  supplierRequestInput: SupplierRequestInput;
  flow: 'wizard' | 'upload';
}

import {
  IconFile,
  IconFileDownload,
  IconFileSpreadsheet,
  IconFileTypeDoc,
  IconFileTypePpt,
  IconPdf,
  IconTrash,
} from '@tabler/icons-react';

interface IProps {
  documents: SupplierRequest['documents'];
  handleChangeValue?: (
    key: keyof SupplierRequestInput,
    value: SupplierRequestInput[keyof SupplierRequestInput]
  ) => void;
  allowDelete?: boolean;
  showDownloadIcon?: boolean;
}

export const UploadedDocuments = ({
  documents = [],
  handleChangeValue,
  allowDelete = true,
  showDownloadIcon = false,
}: IProps) => {
  const [getSupplierRequestDoc, { isLoading: viewFileLoading }] =
    useGetSupplierRequestDocMutation();
  const [deleteSupplierRequestDoc] = useDeleteSupplierRequestDocMutation();
  const [isLoading, setIsLoading] = useState(false);

  const handleFileRemove = async (file: string) => {
    setIsLoading(true);
    const fileToDelete = documents.find((f) => f.fileName === file);
    if (fileToDelete) {
      const res = await deleteSupplierRequestDoc(fileToDelete.key);
      handleChangeValue &&
        handleChangeValue(
          'documents',
          documents.filter((f) => f.fileName !== fileToDelete.fileName)
        );
    }
    setIsLoading(false);
  };

  const handleViewFile = async (key: string) => {
    const res = await getSupplierRequestDoc(key);
    window.open(res.data?.presignedUrl);
  };

  return (
    <div className="w-full pr-4">
      <Text fw={700} size="md" className="mb-6">
        Uploaded Documents:
      </Text>
      <Space h="md" />
      {!documents.length && <Text>No Documents Uploaded</Text>}
      <List spacing="xs" size="sm" center>
        {documents.map((doc) => (
          <List.Item icon={getIcon(doc.fileName)} key={doc.key}>
            <div className=" cursor-pointer flex flex-row items-center">
              <a onClick={() => handleViewFile(doc.key)}>
                <span className="ml-2">{doc.fileName}</span>
              </a>
              {allowDelete &&
                (isLoading ? (
                  <Loader className="ml-4" size="xs" color="red" />
                ) : (
                  <IconTrash
                    className="ml-4 cursor-pointer"
                    size={16}
                    color="red"
                    onClick={() => handleFileRemove(doc.fileName)}
                  />
                ))}
              {showDownloadIcon &&
                (viewFileLoading ? (
                  <Loader className="ml-4" size="xs" />
                ) : (
                  <span className="text-primary-600">
                    <IconFileDownload
                      className="ml-4 cursor-pointer"
                      size={16}
                      onClick={() => handleViewFile(doc.key)}
                    />
                  </span>
                ))}
            </div>
          </List.Item>
        ))}
      </List>
    </div>
  );
};

const getIcon = (file: string) => {
  if (file.endsWith('.pdf')) {
    return <IconPdf size={16} />;
  }
  if (file.endsWith('.xls') || file.endsWith('.xlsx')) {
    return <IconFileSpreadsheet size={16} />;
  }

  if (file.endsWith('.doc') || file.endsWith('.docx')) {
    return <IconFileTypeDoc size={16} />;
  }
  if (file.endsWith('.ppt') || file.endsWith('.pptx')) {
    return <IconFileTypePpt size={16} />;
  }
  return <IconFile size={16} />;
};

export const DocumentUpload = ({
  setCurrentStep,
  handleChangeValue,
  supplierRequestInput,
  flow,
}: DocumentUploadProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const [uploadSupplierRequestDoc] = useUploadSupplierRequestDocMutation();

  const handleFileUpload = async (file: File | null) => {
    setIsLoading(true);
    if (file) {
      const { data } = await uploadSupplierRequestDoc({
        contentType: file.type,
        fileName: file.name,
      });
      const { key, presignedUrl } = data!;
      const upload = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
      });
      if (upload.ok) {
        handleChangeValue('documents', [
          ...supplierRequestInput.documents,
          {
            fileName: file.name,
            key,
            contentType: file.type,
          },
        ]);
      }
    }
    setIsLoading(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <Text fw={700} size="1.5rem" className="mb-6 text-center">
        You can upload any relevant documents for the supplier request here.
      </Text>
      <Space h="sm" />
      {isLoading ? (
        <Loader className="m-auto tex-primary-600" />
      ) : (
        <FileButton
          onChange={handleFileUpload}
          accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
        >
          {(props) => (
            <>
              <button
                {...props}
                className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors w-1/2 m-auto"
              >
                Upload Document
              </button>
              <Text fw={400} size="0.75rem" className="mb-6 text-center">
                Acceptable file types are PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX
              </Text>
            </>
          )}
        </FileButton>
      )}

      <div className="mt-4 flex flex-row">
        <div className="w-1/2 pr-4">
          <UploadedDocuments
            documents={supplierRequestInput.documents}
            handleChangeValue={handleChangeValue}
          />
        </div>
        <div className="w-1/2 pl-4 flex flex-row justify-end">
          <Button
            className="w-[20rem] h-[3rem] mt-auto"
            text="Back"
            variant={isLoading ? 'disabled' : 'secondary'}
            onClick={() => console.log('click')}
          />
          <Space w="md" />
          <Button
            className="w-[20rem] h-[3rem] mt-auto"
            text="Continue"
            variant={isLoading ? 'disabled' : 'primary'}
            suffixIcon="/icons/arrow_right.svg"
            onClick={() => console.log('click')}
          />
        </div>
      </div>
    </div>
  );
};
